import moment from 'moment/moment';
import {
  STREAM_STOP_FAILED,
  STREAM_START_FAILED,
  // below are possible statuses from the server
  PENDING,
  STOPPED,
  STARTED,
  STARTING,
  STOPPING,
} from '@shoflo/studio-redux/constants/StreamStates';
import { getSecondsTo, getServerTime } from '@shoflo/polymer';

/**
 * @deprecated
 * Already moved, use from redux `constants/StreamStates` moving forward
 *
 * @type {{STARTING: string, STOPPED: string, NOT_STREAMING: string, STREAMING: string, STOPPING: string}}
 */
export const STREAM_STATUS = {
  NOT_STREAMING: 'NOT_STREAMING',
  PENDING: 'PENDING',
  STARTING: 'STARTING',
  STREAMING: 'STREAMING',
  STOPPING: 'STOPPING',
  STOPPED: 'STOPPED',
};

export function streamAvailable(streamEndpoints) {
  return streamEndpoints.length > 0;
}

export function isEndpointActive(endpoint) {
  return (
    endpoint.status === STARTED || (endpoint.isStreaming && endpoint.taskId)
  );
}

export function streamingActive(streamEndpoints) {
  return streamEndpoints.some(isEndpointActive);
}

export function isEndpointStopping(endpoint) {
  return (
    endpoint.status === STOPPING ||
    (endpoint.status === STOPPED && endpoint.isStreaming)
  );
}

export function streamStopping(streamEndpoints) {
  return streamEndpoints.some(isEndpointStopping);
}

export function hasEndpointStopped(endpoint) {
  return endpoint.status === STOPPED;
}

export function streamStopped(streamEndpoints) {
  return streamEndpoints.some(hasEndpointStopped);
}

export function streamStartFailed(streamEndpoints) {
  return streamEndpoints.some(
    (endpoint) => endpoint.status === STREAM_START_FAILED
  );
}

export function streamStopFailed(streamEndpoints) {
  return streamEndpoints.some(
    (endpoint) => endpoint.status === STREAM_STOP_FAILED
  );
}

export function isEndpointStarting(endpoint) {
  return (
    endpoint.isStarting ||
    (!!endpoint.taskId && !endpoint.isStreaming) ||
    endpoint.status === STARTING
  );
}

export function streamStarting(streamEndpoints) {
  if (streamingActive(streamEndpoints)) {
    return false;
  }
  return streamEndpoints.some(isEndpointStarting);
}

export function isStartTimeMoreThanFiveMinutes(startOn) {
  return moment(startOn).diff(moment(), 'seconds') > 300;
}

export function isStartTimeLessThanNinetyMinutes(startOn) {
  return (
    !hasStartTimePassed(startOn) &&
    moment(startOn).diff(moment(), 'seconds') <= 60 * 90
  );
}

export function streamPending(streamEndpoints) {
  return streamEndpoints?.some((endpoint) => endpoint.status === PENDING);
}

export function hasStartTimePassed(startOn) {
  return moment().isAfter(moment(startOn));
}

// Endpoint should be provisioned when it is less than 48 hours to startOn
export function endPointShouldBeProvisioned(startOn) {
  return moment(startOn).diff(moment(), 'hours') <= 48;
}

export function hasEndTimePassed(endOn) {
  return moment().isAfter(moment(endOn));
}

export function hasEndpointDisabled(streamEndpoints) {
  return streamEndpoints.some((endpoint) => endpoint.disabled);
}
export function delayedFunction(callback, delayInSeconds, argument) {
  if (delayInSeconds <= 0) {
    return;
  }

  const timeout = setTimeout(() => {
    callback(argument);
    clearTimeout(timeout);
  }, delayInSeconds * 1000);

  return timeout;
}

export function getStoppedReason(streamEndpoints) {
  const endpoint = streamEndpoints.find((endpoint) => !!endpoint.stoppedReason);

  return endpoint ? endpoint.stoppedReason : '';
}

/**
 * useBroadcastTimeRange() from Polymer is not ideal because we need to call it
 * every second inside setInterval. TODO export a function named
 * getBroadcastTimeRange() from Polymer.
 *
 * @param serverTimeDelta
 * @param broadcast
 * @returns {{serverTimeToStart: *, serverTimeToEnd: *}}
 */
export function getBroadcastTimeRange({ serverTimeDelta, broadcast }) {
  const { startOn = 0, endOn = 0 } = broadcast;

  const serverStartTime = getServerTime(startOn, serverTimeDelta);
  const serverEndTime = getServerTime(endOn, serverTimeDelta);

  const serverTimeToStart = getSecondsTo(serverStartTime);
  const serverTimeToEnd = getSecondsTo(serverEndTime);

  return { serverTimeToStart, serverTimeToEnd };
}

export const streamButtonStates = {
  NOT_STARTED_NOT_PROVISIONED: 'NOT_STARTED_NOT_PROVISIONED',
  NOT_STARTED_DISABLED: 'NOT_STARTED_DISABLED',
  NOT_STARTED_ENABLED: 'NOT_STARTED_ENABLED',
  STARTING: 'STARTING',
  STREAMING: 'STREAMING',
  STOPPING: 'STOPPING',
  STOPPED_DISABLED: 'STOPPED_DISABLED',
  STOPPED_ENABLED: 'STOPPED_ENABLED',
};

export function getStreamButtonState(
  streamEndpoints,
  startOn,
  endOn,
  isStreamStopping
) {
  const isStreamAvailable = streamAvailable(streamEndpoints, startOn, endOn);
  const isPastStartTime = hasStartTimePassed(startOn);
  const isPastEndTime = hasEndTimePassed(endOn);
  const isStarting = streamStarting(streamEndpoints);
  const isStreaming = streamingActive(streamEndpoints);
  const isStopping = isStreamStopping || streamStopping(streamEndpoints);
  const isDisabled = hasEndpointDisabled(streamEndpoints);
  const isStopped = streamStopped(streamEndpoints);

  //Occurs when the stream endpoint is explicitly stopped by the host
  // had to move this up top as it takes precedence, as regardless of other conditions, disabled should always be disabled
  if (isDisabled) {
    return streamButtonStates.STOPPED_DISABLED;
  }

  if (!isStreamAvailable && endPointShouldBeProvisioned(startOn)) {
    return streamButtonStates.NOT_STARTED_NOT_PROVISIONED;
  }

  if (isStopping) {
    return streamButtonStates.STOPPING;
  }

  if (isStarting) {
    return streamButtonStates.STARTING;
  }

  if (isStreaming) {
    return streamButtonStates.STREAMING;
  }

  if (isPastStartTime && !isPastEndTime && isStopped) {
    return streamButtonStates.STOPPED_ENABLED;
  }

  //Occurs when the end time for the broadcast is past, and we are not streaming,
  // in which case the button state should be STOPPED_DISABLED
  // Also had to move this up as isPastEndTime would always be true if isPastStartTime is true but not vice versa
  if (isPastEndTime && !isStreaming) {
    return streamButtonStates.STOPPED_DISABLED;
  }

  if (isPastStartTime && !isStreaming) {
    return streamButtonStates.NOT_STARTED_ENABLED;
  }

  if (isStartTimeLessThanNinetyMinutes(startOn)) {
    return streamButtonStates.NOT_STARTED_ENABLED;
  }

  //this is the default...
  return streamButtonStates.NOT_STARTED_DISABLED;
}

export function getStreamState(
  streamEndpoints,
  startOn,
  endOn,
  isStreamStopping
) {
  const isStreamAvailable = streamAvailable(streamEndpoints, startOn, endOn);
  const isStopping = isStreamStopping || streamStopping(streamEndpoints);
  const isDisabled = hasEndpointDisabled(streamEndpoints);
  const isPastStartTime = hasStartTimePassed(startOn);
  const isStreaming = streamingActive(streamEndpoints);
  const isStarting = streamStarting(streamEndpoints);
  const isStopped = streamStopped(streamEndpoints);
  const isPastEndTime = hasEndTimePassed(endOn);

  // Occurs when the stream endpoint is explicitly stopped by the host
  // had to move this up top as it takes precedence, as regardless of
  // other conditions, disabled should always be disabled
  if (isDisabled) return STREAM_STATUS.STOPPED;

  if (!isStreamAvailable && endPointShouldBeProvisioned(startOn)) {
    return STREAM_STATUS.NOT_STREAMING;
  }

  if (isStopping) return STREAM_STATUS.STOPPING;

  if (isStarting) return STREAM_STATUS.STARTING;

  if (isStreaming) return STREAM_STATUS.STREAMING;

  if (isPastStartTime && !isPastEndTime && isStopped) {
    return STREAM_STATUS.NOT_STREAMING;
  }

  // Occurs when the end time for the broadcast is past, and we are not
  // streaming, in which case the state should be STOPPED. Also had to
  // move this up as isPastEndTime would always be true if
  // isPastStartTime is true but not vice versa
  if (isPastEndTime && !isStreaming) return STREAM_STATUS.STOPPED;

  if (isPastStartTime && !isStreaming) return STREAM_STATUS.NOT_STREAMING;

  if (isStartTimeLessThanNinetyMinutes(startOn)) {
    return STREAM_STATUS.NOT_STREAMING;
  }

  // this is the default...
  return STREAM_STATUS.NOT_STREAMING;
}
